import { useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import useTranslator from "../../hooks/useTranslator";
import {
  getApplicationTitle,
  resolveParticipantName,
} from "../../helper/applicationHelpers";
import { Avatar, Icon, Menu, RowButton } from "../design";
import "./ApplicationSelect.css";

/* participant avatars of an application, with the pending-consent note */
const ApplicationPeople = ({ _t, app }) => {
  const participants = app.participants ?? [];
  const pendingCount = participants.filter(
    (p) => p.consent_status === "pending"
  ).length;
  return (
    <span className="applicationSelect__people">
      <span className="applicationSelect__avatars">
        {participants.slice(0, 5).map((p, i) => (
          <span
            key={i}
            className={`applicationSelect__avatarItem${
              p.consent_status === "pending" ? " is-pending" : ""
            }`}
          >
            <Avatar name={resolveParticipantName(p)} size="sm" />
          </span>
        ))}
      </span>
      {pendingCount > 0 && (
        <span className="applicationSelect__pendingText">
          <Icon name="warning" size={14} />
          {_t("pendingConsent")} &bull; {pendingCount}{" "}
          {pendingCount === 1 ? _t("flatmate") : _t("flatmates")}
        </span>
      )}
    </span>
  );
};

const OptionText = ({ title, children }) => (
  <span className="applicationSelect__optionText">
    <span className="applicationSelect__optionTitle">{title}</span>
    {children}
  </span>
);

/**
 * Field that opens the tenant's applications, with "Create new application"
 * as the last row.
 * value: the selected application id, "" for none.
 * onChange(value, app)
 * isDisabled(app): optional; such applications are shown but can't be
 * picked, with disabledTooltip on hover.
 */
const ApplicationSelect = ({
  applications,
  value,
  onChange,
  onCreate,
  placeholder,
  className = "",
  isDisabled,
  disabledTooltip,
}) => {
  const { _t } = useTranslator();
  const [open, setOpen] = useState(false);

  const selectedApp = (applications ?? []).find(
    (app) => String(app._id) === String(value)
  );

  const applicationText = (app) => (
    <OptionText title={getApplicationTitle(app, _t)}>
      <ApplicationPeople _t={_t} app={app} />
    </OptionText>
  );
  const pick = (next, app) => {
    setOpen(false);
    onChange(next, app);
  };

  const blocked = (app) => typeof isDisabled === "function" && isDisabled(app);
  const items = [
    ...(applications ?? []).map((app) => ({
      key: app._id,
      icon: <Avatar name={app.title} size="md" />,
      label: applicationText(app),
      disabled: blocked(app),
      className: `applicationSelect__option${
        selectedApp?._id === app._id ? " is-selected" : ""
      }${blocked(app) ? " applicationSelect__option--blocked" : ""}`,
      onClick: () => pick(app._id, app),
    })),
    {
      key: "create",
      icon: (
        <span className="applicationSelect__plus">
          <Icon name="plus" size={16} />
        </span>
      ),
      label: (
        <OptionText
          title={_t("createNewApplication", "Create new application")}
        >
          <span className="applicationSelect__optionDesc">
            {_t("inviteFlatmatesByEmail", "Invite flatmates by email")}
          </span>
        </OptionText>
      ),
      className: "applicationSelect__option",
      onClick: () => {
        setOpen(false);
        onCreate();
      },
    },
  ];

  return (
    <div className={`applicationSelect ${className}`.trim()}>
      <RowButton
        type="button"
        className={`applicationSelect__trigger${open ? " is-open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {selectedApp ? (
          <>
            <Avatar name={selectedApp.title} size="md" />
            {applicationText(selectedApp)}
          </>
        ) : (
          <span className="applicationSelect__placeholder">{placeholder}</span>
        )}
        <Icon
          name="chevronDown"
          size={20}
          className="applicationSelect__chevron"
        />
      </RowButton>
      {open && <Menu className="applicationSelect__menu" items={items} />}
      {open && disabledTooltip && (applications ?? []).some(blocked) && (
        <ReactTooltip
          anchorSelect=".applicationSelect__option--blocked"
          place="top"
          className="react-tooltip-styling applicationSelect__tooltip"
          // the shared tooltip style colours text inside a <p> only
          render={() => <p>{disabledTooltip}</p>}
        />
      )}
    </div>
  );
};

export default ApplicationSelect;
